'use client'

import React from 'react'
import HeaderFinal from '../components/HeaderFinal'
import Footer from '../components/Footer'
import WhatsAppFloatingButton from '../components/WhatsAppFloatingButton'

// =================== TIPOS Y COMPONENTE HEXÁGONO ===================
type HexOption = {
  id: number | string
  title?: string
  price?: string
  subtitle?: string
  href?: string
  featured?: boolean
}

const HexagonOption = ({ option, size = 'md', color = 'default' }: { option: HexOption; size?: 'sm' | 'md' | 'lg'; color?: 'default' | 'cyan' | 'pink' | 'yellow' | 'ghost' | 'blue' | 'orange' | 'fuchsia' }) => {
  const textClasses = {
    sm: { text: 'text-[10px]', price: 'text-sm'  },
    md: { text: 'text-base',   price: 'text-lg'  },
    lg: { text: 'text-xl',     price: 'text-2xl' },
  }[size];

  const polygonStyle = {
    default: 'fill-white/10 stroke-white/30 stroke-[2px] stroke-dashed',
    cyan:    'fill-cyan-500/50 stroke-cyan-300 stroke-[3px]',
    pink:    'fill-pink-500/50 stroke-pink-300 stroke-[3px]',
    yellow:  'fill-yellow-500/50 stroke-yellow-300 stroke-[3px]',
    ghost:   'fill-transparent stroke-gray-400/60 stroke-[2px]',
    blue:    'fill-blue-500/40 stroke-blue-300 stroke-[3px]',
    orange:  'fill-orange-500/40 stroke-orange-300 stroke-[3px]',
    fuchsia: 'fill-fuchsia-500/40 stroke-fuchsia-300 stroke-[3px]',
  }[color];

  return (
    <div className="relative w-full h-full">
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 115"
        xmlns="http://www.w3.org/2000/svg"
      >
        <polygon
          points="50,0 100,28.75 100,86.25 50,115 0,86.25 0,28.75"
          className={polygonStyle}
        />
      </svg>
      {option.title && (
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-3 text-center">
          <h3 className={`font-bold font-bisteron text-white leading-tight drop-shadow-[0_0_3px_rgba(0,0,0,0.9)] ${textClasses.text}`}>
            {option.title}
          </h3>
          {option.price && (
            <p className={`mt-1 font-bold font-sans drop-shadow-[0_0_3px_rgba(0,0,0,0.9)] ${textClasses.price} ${option.featured ? 'text-pink-300' : 'text-white'}`}>
              {option.price}
            </p>
          )}
          {option.subtitle && (
            <p className="text-[10px] text-gray-300 mt-1 drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">{option.subtitle}</p>
          )}
          {option.featured && (
            <p className="text-[10px] text-pink-300 mt-1 drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">★ Más popular</p>
          )}
        </div>
      )}
    </div>
  );
};

// =================== DATOS PAQUETES CUMPLEAÑOS ===================
const packagesCumple: HexOption[] = [
  { id: 'basico',  title: 'BÁSICO',  price: '$340k', subtitle: 'En pareja' },
  { id: 'premium', title: 'PREMIUM', price: '$365k', subtitle: 'Vuelo premium', featured: true },
  { id: 'pro',     title: 'PRO',     price: '$465k', subtitle: 'Con acrobacias' },
];

const ghost = (id: string): HexOption => ({ id: `ghost-${id}` });

// =================== PANAL DE 3 HEXES PRINCIPALES ===================
// Triángulo equilátero con centros equidistantes del centroide (R=300).
const PanalConPaquetes = () => (
  <div className="relative mx-auto w-full max-w-[900px] aspect-[900/900]">
    <div className="absolute z-30 hex-breathe hex-breathe-1" style={{ top: '-10.93%', left: '26.00%', width: '48%', aspectRatio: '100/115' }}><HexagonOption option={packagesCumple[1]} size="lg" color="orange" /></div>
    <div className="absolute z-30 hex-breathe hex-breathe-2" style={{ top: '39.07%', left: '-2.86%', width: '48%', aspectRatio: '100/115' }}><HexagonOption option={packagesCumple[0]} size="lg" color="blue" /></div>
    <div className="absolute z-30 hex-breathe hex-breathe-3" style={{ top: '39.07%', left: '54.86%', width: '48%', aspectRatio: '100/115' }}><HexagonOption option={packagesCumple[2]} size="lg" color="fuchsia" /></div>
  </div>
);

// =================== LLUVIA FULLSCREEN ===================
const FullscreenRain = () => (
  <div className="rain-fullscreen">
    {/* Capa rápida: 9 hexes medianos */}
    <div className="rain-1" style={{ left: '3%' }}><HexagonOption option={ghost('R1')} color="ghost" size="md" /></div>
    <div className="rain-2" style={{ left: '14%' }}><HexagonOption option={ghost('R2')} color="ghost" size="md" /></div>
    <div className="rain-3" style={{ left: '24%' }}><HexagonOption option={ghost('R3')} color="ghost" size="md" /></div>
    <div className="rain-4" style={{ left: '37%' }}><HexagonOption option={ghost('R4')} color="ghost" size="md" /></div>
    <div className="rain-5" style={{ left: '48%' }}><HexagonOption option={ghost('R5')} color="ghost" size="md" /></div>
    <div className="rain-6" style={{ left: '60%' }}><HexagonOption option={ghost('R6')} color="ghost" size="md" /></div>
    <div className="rain-7" style={{ left: '71%' }}><HexagonOption option={ghost('R7')} color="ghost" size="md" /></div>
    <div className="rain-8" style={{ left: '83%' }}><HexagonOption option={ghost('R8')} color="ghost" size="md" /></div>
    <div className="rain-9" style={{ left: '92%' }}><HexagonOption option={ghost('R9')} color="ghost" size="md" /></div>
    {/* Capa lenta: 6 hexes grandes */}
    <div className="rain-10" style={{ left: '10%' }}><HexagonOption option={ghost('R10')} color="ghost" size="md" /></div>
    <div className="rain-11" style={{ left: '38%' }}><HexagonOption option={ghost('R11')} color="ghost" size="md" /></div>
    <div className="rain-12" style={{ left: '66%' }}><HexagonOption option={ghost('R12')} color="ghost" size="md" /></div>
    <div className="rain-13" style={{ left: '20%' }}><HexagonOption option={ghost('R13')} color="ghost" size="md" /></div>
    <div className="rain-14" style={{ left: '50%' }}><HexagonOption option={ghost('R14')} color="ghost" size="md" /></div>
    <div className="rain-15" style={{ left: '80%' }}><HexagonOption option={ghost('R15')} color="ghost" size="md" /></div>
  </div>
);

export default function Cumpleanos() {
  return (
    <div className="min-h-screen bg-black relative">
      <style jsx global>{`
        /* === CONTENEDOR LLUVIA FULLSCREEN === */
        .rain-fullscreen {
          position: fixed;
          inset: 0;
          z-index: 30;
          pointer-events: none;
          overflow: hidden;
        }
        .rain-fullscreen > div {
          position: absolute;
          top: 0;
          will-change: transform;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
        }

        /* === HEXES DE LLUVIA — TAMAÑOS === */
        .rain-1, .rain-2, .rain-3, .rain-4, .rain-5, .rain-6, .rain-7, .rain-8, .rain-9 {
          width: 10vw; max-width: 180px; aspect-ratio: 100 / 115;
        }
        .rain-3, .rain-7 { width: 12vw; max-width: 220px; }
        .rain-2, .rain-4, .rain-6, .rain-8 { width: 8vw;  max-width: 140px; }
        .rain-10, .rain-11, .rain-12, .rain-13, .rain-14, .rain-15 {
          width: 14vw; max-width: 260px; aspect-ratio: 100 / 115;
        }
        .rain-11, .rain-14 { width: 16vw; max-width: 290px; }
        .rain-10, .rain-13 { width: 18vw; max-width: 320px; }

        /* === DIRECCIONES DE CAÍDA (diagonal) === */
        .rain-2, .rain-4, .rain-6, .rain-8,
        .rain-11, .rain-12, .rain-15 { animation-name: rain-right; }
        .rain-1, .rain-3, .rain-5, .rain-7, .rain-9,
        .rain-10, .rain-13, .rain-14 { animation-name: rain-left; }
        .rain-2  { animation-duration: 14s; animation-delay: -2s; }
        .rain-4  { animation-duration: 15s; animation-delay: -1s; }
        .rain-6  { animation-duration: 13s; animation-delay: -3s; }
        .rain-8  { animation-duration: 10s; animation-delay: -4s; }
        .rain-11 { animation-duration: 25s; animation-delay: -8s; }
        .rain-12 { animation-duration: 28s; animation-delay: -3s; }
        .rain-15 { animation-duration: 22s; animation-delay: -12s; }
        .rain-1  { animation-duration: 12s; animation-delay:  0s; }
        .rain-3  { animation-duration:  9s; animation-delay: -5s; }
        .rain-5  { animation-duration: 11s; animation-delay: -7s; }
        .rain-7  { animation-duration: 16s; animation-delay: -8s; }
        .rain-9  { animation-duration: 17s; animation-delay: -6s; }
        .rain-10 { animation-duration: 30s; animation-delay: -5s; }
        .rain-13 { animation-duration: 26s; animation-delay: -15s; }
        .rain-14 { animation-duration: 24s; animation-delay: -10s; }

        /* === BORDE MÁS GRUESO EN MÓVIL === */
        @media (max-width: 768px) {
          .rain-fullscreen .rain-1 polygon,
          .rain-fullscreen .rain-2 polygon,
          .rain-fullscreen .rain-3 polygon,
          .rain-fullscreen .rain-4 polygon,
          .rain-fullscreen .rain-5 polygon,
          .rain-fullscreen .rain-6 polygon,
          .rain-fullscreen .rain-7 polygon,
          .rain-fullscreen .rain-8 polygon,
          .rain-fullscreen .rain-9 polygon { stroke-width: 8px; }
          .rain-fullscreen .rain-10 polygon,
          .rain-fullscreen .rain-11 polygon,
          .rain-fullscreen .rain-12 polygon,
          .rain-fullscreen .rain-13 polygon,
          .rain-fullscreen .rain-14 polygon,
          .rain-fullscreen .rain-15 polygon { stroke-width: 5px; }
        }

        /* === KEYFRAMES DE LLUVIA DIAGONAL === */
        @keyframes rain-right {
          0%   { transform: translate(-100vw, -10vh); }
          100% { transform: translate(100vw, 110vh); }
        }
        @keyframes rain-left {
          0%   { transform: translate(100vw, -10vh); }
          100% { transform: translate(-100vw, 110vh); }
        }

        /* === RESPIRACIÓN DE LOS 3 HEXES PRINCIPALES === */
        .hex-breathe {
          transform-origin: center center;
          will-change: transform;
          animation-iteration-count: infinite;
        }
        .hex-breathe-1 { animation-name: breathe; animation-duration: 3.0s; animation-delay: 0s;    }
        .hex-breathe-2 { animation-name: breathe; animation-duration: 3.0s; animation-delay: -1.0s; }
        .hex-breathe-3 { animation-name: breathe; animation-duration: 3.0s; animation-delay: -2.0s; }
        @keyframes breathe {
          0%      { transform: scale(1.00); animation-timing-function: linear; }
          66.67%  { transform: scale(1.00); animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1); }
          83.33%  { transform: scale(1.08); animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1); }
          100%    { transform: scale(1.00); }
        }

        /* === ACCESIBILIDAD === */
        @media (prefers-reduced-motion: reduce) {
          .hex-breathe { animation: none !important; }
          .rain-fullscreen > div { animation: none !important; }
        }
      `}</style>

      <HeaderFinal />

      {/* LLUVIA HEXAGONAL FULLSCREEN (encima del video, debajo del contenido) */}
      <FullscreenRain />

      {/* VIDEO DE FONDO FIJO */}
      <div className="fixed inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-auto h-[100vh] max-w-none opacity-70"
          style={{ height: '100vh', width: 'auto' }}
        >
          <source src="/videos/video_cumple_optimized_v3.mp4" type="video/mp4" />
          Tu navegador no soporta el elemento de video.
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black"></div>
      </div>

      <main className="pt-24 pb-20 relative z-10">
        {/* Hero Section — Panal hexagonal */}
        <section className="py-12 relative">
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-8">
              <div className="text-5xl mb-2">🎂</div>
              <h1 className="text-3xl md:text-5xl font-bold font-bisteron text-white mb-3 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                Cumpleaños en las <span className="text-pink-400">Nubes</span>
              </h1>
              <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                Celebra tu cumpleaños de una forma que NUNCA olvidarás. Vuela en parapente sobre los Andes.
              </p>
            </div>
            <PanalConPaquetes />
          </div>
        </section>

        {/* Paquetes de Cumpleaños */}
        <section className="py-16 relative">
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-bisteron text-white mb-4 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                Paquetes de <span className="text-pink-400">Cumpleaños</span>
              </h2>
              <p className="text-gray-300 max-w-2xl mx-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                Elige cómo quieres celebrar
              </p>
            </div>

            {/* DESCRIPCION DETALLADA DE CADA PAQUETE */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {/* BASICO */}
              <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden hover:border-pink-500/50 transition-all duration-300">
                <div className="p-8">
                  <div className="text-center mb-6">
                    <div className="text-5xl mb-4">🎈</div>
                    <h3 className="text-2xl font-bold text-white mb-2">BÁSICO</h3>
                    <p className="text-gray-400 text-sm">Perfecto para celebrar en pareja</p>
                  </div>
                  
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-pink-400 mb-2">$340,000</div>
                    <p className="text-gray-500 text-xs">por persona (IVA incluido)</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 mb-6">
                    <p className="text-white text-sm font-semibold mb-2">Incluye:</p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Vuelo Recreativo MAX (15-20 min)
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Equipo completo
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Instructor certificado FEDEAEREOS
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Seguro de vuelo incluido
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Video GoPro
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Aviso en tierra "Feliz Cumpleaños"
                      </li>
                    </ul>
                  </div>

                  <a
                    href="https://wa.me/573028309637?text=Hola!%20Quiero%20reservar%20el%20Paquete%20BASICO%20de%20Cumpleanos%20🎂"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white text-center font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Reservar Básico
                  </a>
                </div>
              </div>

              {/* PREMIUM */}
              <div className="bg-gradient-to-b from-pink-500/20 to-purple-500/20 rounded-2xl border-2 border-pink-500 overflow-hidden transform scale-105 shadow-2xl shadow-pink-500/20">
                <div className="bg-pink-500 text-center py-2">
                  <span className="text-white font-bold text-sm">⭐ MÁS POPULAR</span>
                </div>
                <div className="p-8">
                  <div className="text-center mb-6">
                    <div className="text-5xl mb-4">🎉</div>
                    <h3 className="text-2xl font-bold text-white mb-2">PREMIUM</h3>
                    <p className="text-gray-400 text-sm">Para celebrar con amigos y familia</p>
                  </div>
                  
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-pink-400 mb-2">$365,000</div>
                    <p className="text-gray-500 text-xs">por persona (IVA incluido)</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 mb-6">
                    <p className="text-white text-sm font-semibold mb-2">Incluye TODO del BÁSICO +</p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Mesa decorada
                      </li>
                    </ul>
                  </div>

                  <a
                    href="https://wa.me/573028309637?text=Hola!%20Quiero%20reservar%20el%20Paquete%20PREMIUM%20de%20Cumpleanos%20🎉"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white text-center font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Reservar Premium
                  </a>
                </div>
              </div>

              {/* PRO */}
              <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden hover:border-pink-500/50 transition-all duration-300">
                <div className="p-8">
                  <div className="text-center mb-6">
                    <div className="text-5xl mb-4">🚀</div>
                    <h3 className="text-2xl font-bold text-white mb-2">PRO</h3>
                    <p className="text-gray-400 text-sm">La experiencia EXTREMA de cumpleaños</p>
                  </div>
                  
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-pink-400 mb-2">$465,000</div>
                    <p className="text-gray-500 text-xs">por persona (IVA incluido)</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 mb-6">
                    <p className="text-white text-sm font-semibold mb-2">Incluye TODO del PREMIUM +</p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Mesa decorada premium
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Vuelo PRO MAX con acrobacias (20-25 min)
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Acrobacias: Delfines, Wing overs, Barrenas
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Instructor especialista en acrobacias
                      </li>
                    </ul>
                  </div>

                  <a
                    href="https://wa.me/573028309637?text=Hola!%20Quiero%20reservar%20el%20Paquete%20PRO%20de%20Cumpleanos%20🚀"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white text-center font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Reservar Pro
                  </a>
                </div>
              </div>
            </div>

            {/* TABLA COMPARATIVA */}
            <div className="max-w-7xl mx-auto mb-16">
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-pink-500 scrollbar-track-gray-800">
                <div className="bg-black/80 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden min-w-[800px]">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-pink-500/20 to-purple-500/20">
                        <th className="p-6 text-left text-white font-bold text-lg border-b border-white/10">Servicio / Paquete</th>
                        <th className="p-6 text-center text-white font-bold text-lg border-b border-white/10">
                          <div className="text-3xl mb-2">🎈</div>
                          BÁSICO
                          <div className="text-pink-400 font-bold mt-1">$340,000</div>
                        </th>
                        <th className="p-6 text-center text-white font-bold text-lg border-b border-white/10 bg-pink-500/10">
                          <div className="text-3xl mb-2">🎉</div>
                          PREMIUM
                          <div className="text-pink-400 font-bold mt-1">$365,000</div>
                          <div className="text-xs text-green-400 mt-1">⭐ MÁS POPULAR</div>
                        </th>
                        <th className="p-6 text-center text-white font-bold text-lg border-b border-white/10">
                          <div className="text-3xl mb-2">🚀</div>
                          PRO
                          <div className="text-pink-400 font-bold mt-1">$465,000</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Tipo de Vuelo</td>
                        <td className="p-4 text-center text-sm">Recreativo MAX (15-20 min)</td>
                        <td className="p-4 text-center text-sm bg-pink-500/5">Recreativo MAX (15-20 min)</td>
                        <td className="p-4 text-center text-sm">Recreativo PRO MAX (20-25 min)</td>
                      </tr>
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Equipo completo</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl bg-pink-500/5">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Instructor certificado FEDEAEREOS</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl bg-pink-500/5">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Seguro de vuelo incluido</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl bg-pink-500/5">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Video GoPro incluido</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl bg-pink-500/5">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Aviso en tierra "Feliz Cumpleaños"</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl bg-pink-500/5">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Mesa decorada</td>
                        <td className="p-4 text-center text-gray-600 text-xl">❌</td>
                        <td className="p-4 text-center text-green-400 text-xl bg-pink-500/5">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Acrobacias</td>
                        <td className="p-4 text-center text-gray-600 text-xl">❌</td>
                        <td className="p-4 text-center text-gray-600 text-xl bg-pink-500/5">❌</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                      <tr className="border-t border-white/10">
                        <td className="p-4 font-semibold text-white">Instructor especialista</td>
                        <td className="p-4 text-center text-gray-600 text-xl">❌</td>
                        <td className="p-4 text-center text-gray-600 text-xl bg-pink-500/5">❌</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                      <tr className="border-t border-white/10 bg-green-500/10">
                        <td className="p-4 font-semibold text-white">IVA incluido</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl bg-pink-500/5">✅</td>
                        <td className="p-4 text-center text-green-400 text-xl">✅</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 relative">
          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold font-bisteron text-white mb-4 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              ¿Listo para el Mejor Cumpleaños de tu Vida?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              Reserva con al menos 3 días de anticipación. Grupos grandes requieren reserva anticipada.
            </p>
            <a
              href="https://wa.me/573028309637?text=Hola!%20Quiero%20reservar%20mi%20Cumpleanos%20en%20las%20Nubes%20🎂"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-pink-500/25"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z"/>
              </svg>
              Reservar Mi Cumpleaños
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloatingButton />
    </div>
  )
}